import React from "react";
import RequestPreview from "components/RequestPreview";
import CodePreview from "components/CodePreview";
import DescriptionList from "components/DescriptionList";
import Paragraphs from "components/Paragraphs";
import { useAuthedDocsPreview } from "utils/hooks/docs";
import isEmpty from "lodash/isEmpty";
import { CODE_PREVIEW_MAX_HEIGHT, TABLE_TYPE } from "constants/docs";
import NextPrevNav from "components/NextPrevNav";
import Chat from "components/MDX/Chat";
import ListNodes from "components/ListNodes";
import DiscordLink from "components/DiscordLink";
import { getJWT } from "utils/auth";
import APIKeysAccessPrompt from "components/APIKeysAccessPrompt";
import Padder from "components/Padder";
import { useScrollHashTargetIntoView } from "utils/hooks/view";

const DefinitionTable = ({ title, columns, rows }) => {
  return (
    <div className="w-100 bg-white ba hairline-1 br3 shadow-0">
      <div className="pa2 ma1 fw6 f7 ttu">{title}</div>
      <div className="bt w-100 hairline-1 flex flex-column">
        <table className="f6 w-100 mw8 center" cellSpacing="0">
          <tbody className="lh-copy">
            {rows.map((row, rowIx) => (
              <tr key={rowIx}>
                {row.map((cell, cellIx) =>
                  cellIx ? (
                    <th key={cellIx} className="fw3 tl pa2 v-top">
                      <span>{cell}</span>
                    </th>
                  ) : (
                    <td key={cellIx} className="fw6 pa2 tl v-top">
                      {cell}
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TABLE_COMPONENTS = {
  [TABLE_TYPE.DEFINITION]: DefinitionTable
};

/**
 * Need to get NavConfig in here
 * -
 * - <Padder theme="visible" />
 */
const ResourceMethod = ({
  navConfig,
  pathname,
  description: { title, subtitle, data, paragraphs },
  preview: { request, code, tables }
}) => {
  const { loading, preview: authedPreview } = useAuthedDocsPreview({
    pathname
  });
  useScrollHashTargetIntoView();

  const needsTestKeys = isEmpty(authedPreview);
  const isUser = Boolean(getJWT());

  const contentNodes = [];
  if (paragraphs) {
    contentNodes.push(
      <Paragraphs
        paragraphs={paragraphs}
        customClasses={{
          block: "lh-copy pt1 f5"
        }}
      />
    );
  }
  if (data) {
    contentNodes.push(<DescriptionList {...data} />);
  }

  contentNodes.push(
    <Chat
      header=""
      subheader="Have any questions or feedback?"
      actionCopy="Chat"
    >
      <DiscordLink copy="or join us on Discord" />
    </Chat>
  );

  if (navConfig) {
    contentNodes.push(
      <div className="bt w-100 hairline-1 pt3">
        <NextPrevNav
          config={navConfig}
          customClasses={{
            directionLabel: "f6 black-70",
            parentLabel: "fw5 f5 pt2",
            nextLabel: "f6 fw6 color-primary"
          }}
        />
      </div>
    );
  }

  const sidebarNodes = [];
  const ctxRequest =
    !isEmpty(authedPreview) && authedPreview.request
      ? authedPreview.request
      : request;
  if (ctxRequest) {
    sidebarNodes.push(<RequestPreview {...ctxRequest} loading={loading} />);
  }

  sidebarNodes.push(
    <APIKeysAccessPrompt needsTestKeys={needsTestKeys} isUser={isUser} />
  );

  if (code) {
    const maxHeight = CODE_PREVIEW_MAX_HEIGHT[`${sidebarNodes.length}`];
    sidebarNodes.push(
      <CodePreview {...code} loading={loading} maxHeight={maxHeight} />
    );
  }
  if (Array.isArray(tables)) {
    tables.forEach((table) => {
      const TableComp = TABLE_COMPONENTS[table.type];
      if (TableComp) {
        sidebarNodes.push(<TableComp {...table} />);
      }
    });
  }

  sidebarNodes.push(<Padder key="padder" />);

  return (
    <div className="flex flex-column items-center w-100 pt4-l">
      <div className="flex flex-column pa4-l w-100 flex-row-l">
        <div
          style={{ flexShrink: 0 }}
          className="pa0 pa3-l pt4 pt3-l w-100 w-50-l"
        >
          <h3 className="f3 fw6 pb3 ma0">{title}</h3>
          <div className="f5 fw4 pb3 lh-copy">{subtitle}</div>
          <ListNodes nodes={contentNodes} />
        </div>
        <div
          style={{
            flexGrow: 1,
            position: "sticky"
          }}
          className="bg-white ph3-l pb3 pv4 w-100 w-50-l dib self-start sticky top-0"
        >
          <div className="dib w-100">
            <ListNodes nodes={sidebarNodes} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceMethod;
